import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CheckCheck, ChevronRight, Eye, EyeOff, KeyRound, LogOut } from 'lucide-react';

import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { changeLanguage } from '@/lib/i18n';
import { mustChangePassword as needsNewPassword } from '@/lib/constants';
import { ConfirmDialog, Spinner, StatusPill } from '@/components/phone/primitives';
import { Colors } from '@/components/phone/theme';

/**
 * The account, in the app's own language.
 *
 * The identity as a plain bordered card, every role the account holds rather
 * than the primary one alone, then rows — the same row, repeated, one per
 * destination — and the quiet ones (language, password, logout) after the work
 * one. A supervisor's day ends by checking what they passed, so that row is
 * first and it is a row, not a buried accordion.
 *
 * Mirrors `mobile-frontend/app/(tabs)/profile.tsx`.
 */

const LANGS = [
  { key: 'en', label: 'English' },
  { key: 'hi', label: 'हिंदी' },
  { key: 'pa', label: 'ਪੰਜਾਬੀ' },
];

const ROLES = {
  supervisor: 'Supervisor', machine: 'Machine', machine_man: 'Machine', driver: 'Driver',
  binder: 'Binder', admin: 'Admin', head_designer: 'Head Designer', designer: 'Designer',
  city_office_manager: 'City Manager', accountant: 'Accountant', dispatcher: 'Dispatcher',
  receptionist: 'Receptionist', store_keeper: 'Store Keeper',
};

const MIN_PASSWORD_LENGTH = 4;

/** One tappable destination. Every row on this screen is this row. */
function Row({ icon: Icon, label, hint, onClick, testID, tone = 'default' }) {
  const danger = tone === 'danger';
  const ink = danger ? Colors.status.error : Colors.text.secondary;
  return (
    <button
      type="button"
      data-testid={testID}
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-[10px] border px-4 py-3.5 text-left"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <span
        className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-md"
        style={{ backgroundColor: danger ? Colors.semantic.criticalWash : Colors.background.secondary }}
      >
        <Icon className="h-[18px] w-[18px]" style={{ color: ink }} />
      </span>
      <span className="min-w-0 flex-1">
        <span
          className="block text-[15px] font-semibold"
          style={{ color: danger ? Colors.status.error : Colors.text.primary }}
        >
          {label}
        </span>
        {!!hint && (
          <span className="block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>{hint}</span>
        )}
      </span>
      <ChevronRight className="h-4 w-4 shrink-0" style={{ color: ink }} />
    </button>
  );
}

export default function SupervisorProfilePage() {
  const { user, logout, checkAuth } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const mustChange = needsNewPassword(user);
  const [showPassword, setShowPassword] = useState(mustChange);
  const [currentPass, setCurrentPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [reveal, setReveal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmingLogout, setConfirmingLogout] = useState(false);

  const roles = user?.roles?.length ? user.roles : [user?.role].filter(Boolean);
  const initial = user?.full_name?.charAt(0)?.toUpperCase() || '?';

  const handleLanguageChange = async (lang) => {
    changeLanguage(lang);
    try { await api.put('/profile/language', { language: lang }); } catch { /* the app language is set either way */ }
  };

  const handleChangePassword = async () => {
    if (!currentPass || !newPass || !confirmPass) {
      toast.error('Fill all three password fields');
      return;
    }
    if (newPass !== confirmPass) {
      toast.error('The two new passwords do not match');
      return;
    }
    if (newPass.length < MIN_PASSWORD_LENGTH) {
      toast.error(`A password is at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/auth/change-password', {
        current_password: currentPass,
        new_password: newPass,
      });
      // the change retires every earlier token, this session's included
      if (res.data?.token) localStorage.setItem('leroy_token', res.data.token);
      await checkAuth();
      toast.success('Password changed');
      setCurrentPass(''); setNewPass(''); setConfirmPass(''); setShowPassword(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not change it');
    }
    setSaving(false);
  };

  const passwordField = (value, set, placeholder, testID) => (
    <input
      data-testid={testID}
      className="h-12 w-full rounded-[10px] border px-4 text-[15px] outline-none"
      style={{
        backgroundColor: Colors.background.primary,
        borderColor: Colors.border.default,
        color: Colors.text.primary,
      }}
      value={value}
      onChange={(e) => set(e.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
      type={reveal ? 'text' : 'password'}
      autoCapitalize="none"
    />
  );

  return (
    <div className="flex flex-1 flex-col px-5 pb-8">
      <div className="pb-4 pt-6">
        <h1 className="text-[22px] font-bold -tracking-[0.5px]" style={{ color: Colors.text.primary }}>
          {t('profile.title')}
        </h1>
      </div>

      {/* who this account is — one card, no gradient, nothing decorative */}
      <div
        className="rounded-[10px] border p-4"
        style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
      >
        <div className="flex items-center gap-3">
          <span
            className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-md border text-[19px] font-bold"
            style={{
              backgroundColor: Colors.accent.wash,
              borderColor: Colors.border.default,
              color: Colors.accent.DEFAULT,
            }}
          >
            {initial}
          </span>
          <div className="min-w-0 flex-1">
            <p className="line-clamp-2 text-[17px] font-bold -tracking-[0.3px]" style={{ color: Colors.text.primary }}>
              {user?.full_name}
            </p>
            <p className="truncate text-[13px]" style={{ color: Colors.text.tertiary }}>@{user?.username}</p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {roles.map((role) => (
            <StatusPill
              key={role}
              testID={`role-${role}`}
              label={ROLES[role] || role}
              tone={role === user?.role ? 'accent' : 'neutral'}
            />
          ))}
        </div>
        {(!!user?.machine_type || !!user?.mobile_number) && (
          <p className="mt-2 truncate text-[11px] capitalize" style={{ color: Colors.text.tertiary }}>
            {[user?.machine_type?.replace(/_/g, ' '), user?.mobile_number].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>

      {/* the supervisor's own record of what they moved */}
      <section className="mt-6">
        <p className="mb-2 text-[10px] font-medium uppercase tracking-[1.2px]" style={{ color: Colors.text.tertiary }}>
          {t('profile.my_record')}
        </p>
        <Row
          testID="passed-history-btn"
          icon={CheckCheck}
          label={t('supervisor.passed_history')}
          hint={t('supervisor.passed_history_hint')}
          onClick={() => navigate('/supervisor/history')}
        />
      </section>

      <section className="mt-6">
        <p className="mb-2 text-[10px] font-medium uppercase tracking-[1.2px]" style={{ color: Colors.text.tertiary }}>
          {t('profile.language')}
        </p>
        <div className="flex gap-2">
          {LANGS.map((lang) => {
            const on = i18n.language === lang.key;
            return (
              <button
                key={lang.key}
                type="button"
                data-testid={`lang-${lang.key}`}
                aria-pressed={on}
                onClick={() => handleLanguageChange(lang.key)}
                className="flex-1 rounded-[10px] border py-3 text-[13px] font-semibold"
                style={{
                  backgroundColor: on ? Colors.accent.DEFAULT : Colors.background.primary,
                  borderColor: on ? Colors.accent.DEFAULT : Colors.border.default,
                  color: on ? Colors.text.inverse : Colors.text.secondary,
                }}
              >
                {lang.label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="mt-6">
        <p className="mb-2 text-[10px] font-medium uppercase tracking-[1.2px]" style={{ color: Colors.text.tertiary }}>
          {t('profile.security')}
        </p>
        {mustChange && (
          <p
            data-testid="must-change-password-notice"
            className="mb-2 text-[13px] font-medium"
            style={{ color: Colors.status.error }}
          >
            {t('profile.must_change')}
          </p>
        )}
        <Row
          testID="toggle-password-btn"
          icon={KeyRound}
          label={t('profile.change_password')}
          onClick={() => setShowPassword(!showPassword)}
        />
        {showPassword && (
          <div className="mt-2 flex flex-col gap-2">
            {passwordField(currentPass, setCurrentPass, t('profile.current_password'), 'current-pass-input')}
            {passwordField(newPass, setNewPass, t('profile.new_password'), 'new-pass-input')}
            {passwordField(confirmPass, setConfirmPass, t('profile.confirm_password'), 'confirm-pass-input')}
            <button
              type="button"
              data-testid="reveal-pass-btn"
              onClick={() => setReveal(!reveal)}
              className="flex items-center gap-1.5 py-0.5 text-[11px] font-medium"
              style={{ color: Colors.text.secondary }}
            >
              {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {reveal ? t('profile.hide_passwords') : t('profile.show_passwords')}
            </button>
            <button
              type="button"
              data-testid="save-password-btn"
              onClick={handleChangePassword}
              disabled={saving}
              className="flex h-12 items-center justify-center rounded-[10px] text-[15px] font-bold disabled:opacity-60"
              style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
            >
              {saving ? <Spinner size="sm" className="!border-white !border-b-transparent" /> : t('common.save')}
            </button>
          </div>
        )}
      </section>

      <section className="mt-6">
        <Row
          testID="logout-btn"
          icon={LogOut}
          label={t('profile.logout')}
          tone="danger"
          onClick={() => setConfirmingLogout(true)}
        />
      </section>

      <p className="mt-6 text-center text-[11px] tracking-[0.6px]" style={{ color: Colors.text.disabled }}>
        LRPP Portal · v1.0.0
      </p>

      <ConfirmDialog
        open={confirmingLogout}
        title={t('profile.logout')}
        message={t('profile.confirm_logout')}
        confirmLabel={t('common.yes')}
        cancelLabel={t('common.cancel')}
        destructive
        onConfirm={() => { setConfirmingLogout(false); logout(); navigate('/login', { replace: true }); }}
        onCancel={() => setConfirmingLogout(false)}
      />
    </div>
  );
}
